import * as React from "react"
import { useTranslation } from "react-i18next"

import { useAuth } from "@/auth/useAuth"
import { ArrowLeftIcon } from "@/components/icons"
import { Button } from "@/components/ui-kit"
import { CustomerInfo } from "@/components/ui-kit/customer-info"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { getApiErrorMessage } from "@/lib/error-codes"
import { cn } from "@/lib/utils"
import type { Customer } from "@/types/customer"

export type CustomerSelectFormHandle = {
  submit: () => Promise<boolean>
}

type CustomerSelectFormProps = {
  customers: Customer[]
  value: string
  onValueChange: (value: string) => void
}

export const CustomerSelectForm = React.forwardRef<
  CustomerSelectFormHandle,
  CustomerSelectFormProps
>(function CustomerSelectForm({ customers, value, onValueChange }, ref) {
  const { t } = useTranslation()
  const { selectCustomer, selectedCustomer, logout } = useAuth()
  const [submitError, setSubmitError] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [isLoggingOut, setIsLoggingOut] = React.useState(false)
  const isSingleCustomer = customers.length === 1

  const handleBackToLogin = async () => {
    setIsLoggingOut(true)

    try {
      await logout()
    } finally {
      setIsLoggingOut(false)
    }
  }

  React.useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        if (!value) {
          return false
        }

        const customerId = Number(value)
        if (selectedCustomer?.customerId === customerId) {
          return true
        }

        setSubmitError(null)
        setIsSubmitting(true)

        try {
          await selectCustomer(customerId)
          return true
        } catch (error) {
          setSubmitError(getApiErrorMessage(error))
          return false
        } finally {
          setIsSubmitting(false)
        }
      },
    }),
    [selectCustomer, selectedCustomer?.customerId, value],
  )

  return (
    <div className="flex flex-col px-2 py-6 lg:h-full lg:p-8 lg:pb-24">
      <Button
        variant="link"
        type="button"
        className="mb-4 w-fit px-0 py-0 text-base text-muted-foreground"
        onClick={() => void handleBackToLogin()}
        disabled={isLoggingOut || isSubmitting}
      >
        <span className="inline-flex items-center gap-2">
          <span aria-hidden>
            <ArrowLeftIcon size={18} />
          </span>
          <span className="leading-[1]">{t("customerSelect.backToLogin")}</span>
        </span>
      </Button>

      <h2 className="mb-3 text-[28px] leading-[36px] font-bold text-foreground lg:text-[48px] lg:leading-[52px]">
        {t(isSingleCustomer ? "customerSelect.singleTitle" : "customerSelect.title")}
      </h2>
      <p className="mb-6 text-[16px] leading-[22px] text-muted-foreground lg:mb-8 lg:text-lg lg:leading-6">
        {t(isSingleCustomer ? "customerSelect.singleDescription" : "customerSelect.description")}
      </p>

      <RadioGroup
        value={value}
        onValueChange={(nextValue) => {
          onValueChange(nextValue)
          setSubmitError(null)
        }}
        className="flex flex-col gap-2"
      >
        {customers.map((customer) => {
          const itemId = `rent-customer-${customer.customerId}`

          return (
            <div key={customer.customerId}>
              <RadioGroupItem id={itemId} value={String(customer.customerId)} className="peer sr-only hidden" />
              <Label
                htmlFor={itemId}
                className={cn(
                  "flex min-h-[80px] cursor-pointer flex-col items-start justify-center rounded-[4px] border-2 border-transparent bg-input p-3 lg:min-h-[92px]",
                  "hover:border-brand hover:bg-card",
                  "peer-data-[state=checked]:pointer-events-none peer-data-[state=checked]:border-brand peer-data-[state=checked]:bg-card",
                )}
              >
                <CustomerInfo
                  customerType={customer.customerType}
                  name={customer.name}
                  email={customer.email}
                  phone={customer.phone}
                />
              </Label>
            </div>
          )
        })}
      </RadioGroup>

      {submitError ? <p className="mt-4 text-sm text-destructive">{submitError}</p> : null}
      {isSubmitting ? <p className="mt-4 text-sm text-light-muted">Загружаем...</p> : null}
    </div>
  )
})
